import { base44 } from '@/api/base44Client';
import { calculateStageOverlap } from './productionOverlap';

/**
 * Single Continuous Task Progression Pipeline with Parallel Overlap Support:
 * - Mixing (racik) -> QC Validasi -> Melanjutkan ke Filling
 * - Filling (in_progress) -> Tim Pengemasan dapat mulai paralel tanpa menunggu filling beres
 * - Filling Selesai -> Validasi QC Filling (Pengemasan tetap lanjut)
 * - Pengemasan Selesai -> Validasi QC Akhir -> Selesai Penuh (PO: 'siap_kirim')
 */

/**
 * Memulai tahap Pengemasan secara paralel saat Filling masih berjalan
 */
export async function startParallelPacking(task, member) {
  if (!task || !task.id || !member) return;

  const now = new Date().toISOString();
  const brandPrefix = (task.po_brand || "PO").split("/")[0].trim();

  const prevSubStages = task.sub_stages || {};
  const currentFilling = prevSubStages.filling || {
    status: task.status === "in_progress" && task.activity_type === "filling" ? "in_progress" : "assigned",
    start_time: task.start_time || now,
    pic_id: task.pic_id,
    pic_name: task.pic_name,
  };

  const newSubStages = {
    ...prevSubStages,
    filling: currentFilling,
    packing: {
      status: "in_progress",
      start_time: now,
      pic_id: member.id,
      pic_name: member.full_name,
      pic_role: member.role || "staff",
    },
  };

  const updatedActiveStages = Array.from(new Set([...(task.active_stages || ["filling"]), "packing"]));

  const updatePayload = {
    sub_stages: newSubStages,
    active_stages: updatedActiveStages,
    status: "in_progress",
    task_name: `${brandPrefix} - Filling & Pengemasan (Paralel)`,
    notes: task.notes
      ? `${task.notes}\n[${new Date().toLocaleTimeString('id-ID')}] Pengemasan dimulai bersamaan oleh ${member.full_name}`
      : `[${new Date().toLocaleTimeString('id-ID')}] Pengemasan dimulai bersamaan oleh ${member.full_name}`,
  };

  await base44.entities.ProductionTask.update(task.id, updatePayload);

  // Update PO jika ada
  if (task.po_id) {
    await base44.entities.ProductionOrder.update(task.po_id, {
      status: "filling_packing",
    }).catch(() => {});
  }
}

/**
 * Menyelesaikan sub-stage tertentu (misal: Filling atau Pengemasan)
 */
export async function completeSubStage(task, stageKey, completedQty, member, qcPayload = null) {
  if (!task || !task.id) return;

  const now = new Date().toISOString();
  const prevSubStages = task.sub_stages || {};
  const stageData = prevSubStages[stageKey] || {};

  const startTime = stageData.start_time || task.start_time || now;
  const grossMinutes = Math.round((new Date(now).getTime() - new Date(startTime).getTime()) / 60000);
  const pauseMinutes = stageData.total_pause_minutes || 0;
  const netMinutes = Math.max(1, grossMinutes - pauseMinutes);

  const updatedStageData = {
    ...stageData,
    status: "completed",
    end_time: now,
    duration_minutes: netMinutes,
    gross_minutes: grossMinutes,
    completed_quantity: Number(completedQty) || task.target_quantity || 0,
    qc_data: qcPayload,
  };

  const updatedSubStages = {
    ...prevSubStages,
    [stageKey]: updatedStageData,
  };

  // Cek apakah stage lain masih berjalan
  const remainingActiveStages = (task.active_stages || [task.activity_type || "filling"]).filter(
    (s) => s !== stageKey
  );

  let newOverallStatus = task.status;
  let nextTaskName = task.task_name;

  // Jika Filling selesai tapi Packing masih jalan:
  if (stageKey === "filling") {
    if (updatedSubStages.packing?.status === "in_progress") {
      newOverallStatus = "in_progress";
      nextTaskName = `${(task.po_brand || "PO").split("/")[0].trim()} - Pengemasan (Filling Selesai)`;
    } else if (updatedSubStages.packing?.status === "completed") {
      newOverallStatus = "menunggu_qc"; // siap QC akhir
    } else {
      // Packing belum mulai -> Siapkan transisi ke pengemasan
      newOverallStatus = "assigned";
      nextTaskName = `${(task.po_brand || "PO").split("/")[0].trim()} - Pengemasan`;
    }
  }

  // Jika Packing selesai:
  if (stageKey === "packing") {
    newOverallStatus = "menunggu_qc"; // Masuk ke validasi QC akhir
    nextTaskName = `${(task.po_brand || "PO").split("/")[0].trim()} - Menunggu Validasi QC Akhir`;
  }

  // Hitung overlap info jika both filling & packing data exist
  let overlapData = null;
  if (updatedSubStages.filling && updatedSubStages.packing) {
    overlapData = calculateStageOverlap(updatedSubStages.filling, updatedSubStages.packing);
  }

  const updatePayload = {
    sub_stages: updatedSubStages,
    active_stages: remainingActiveStages,
    status: newOverallStatus,
    task_name: nextTaskName,
    overlap_info: overlapData,
  };

  await base44.entities.ProductionTask.update(task.id, updatePayload);
}

/**
 * Auto-advance single sequential task (legacy or standard transition)
 */
export async function autoAdvanceNextTask(currentTask, completedQty = 0) {
  if (!currentTask || !currentTask.id) return;

  try {
    const poId = currentTask.po_id;
    const currAct = (currentTask.activity_type || '').toLowerCase();
    let brandName = currentTask.po_brand || 'PO Produksi';

    if (poId) {
      const orders = await base44.entities.ProductionOrder.filter({ id: poId });
      if (orders && orders.length > 0) {
        const po = orders[0];
        brandName = po.brand_name || po.customerName || po.nama_brand || brandName;
      }
    }

    const variantName = currentTask.product_name || currentTask.variant_name;
    const allPoTasks = poId ? (await base44.entities.ProductionTask.filter({ po_id: poId }) || []) : [];

    if (currAct === 'racik' || currAct === 'mixing') {
      if (poId) {
        await base44.entities.ProductionOrder.update(poId, { status: 'menunggu_filling' }).catch(() => {});
      }

      const bottleSize = Number(currentTask.botol_ukuran_aktual || currentTask.ukuran_ml || 30);
      const isSourceLiter = (currentTask.unit || '').toLowerCase().includes('l') || (currentTask.unit || '').toLowerCase().includes('liter');
      const fillingTargetQty = isSourceLiter 
        ? Math.round((Number(currentTask.target_quantity) || 0) * 1000 / bottleSize)
        : Math.round(Number(currentTask.target_quantity) || 1);

      // Cek apakah tugas filling sudah ada untuk PO & varian ini (Cegah Duplikasi)
      const existingFilling = allPoTasks.find(t => 
        t.id !== currentTask.id &&
        t.activity_type === 'filling' &&
        (!variantName || !t.product_name || t.product_name.toLowerCase().trim() === variantName.toLowerCase().trim())
      );

      if (existingFilling) {
        // Jika sudah ada, cukup sinkronkan target & statusnya tanpa membuat duplikat baru
        await base44.entities.ProductionTask.update(existingFilling.id, {
          target_quantity: fillingTargetQty > 0 ? fillingTargetQty : existingFilling.target_quantity,
          unit: 'pcs',
          botol_ukuran_aktual: bottleSize,
        });
      } else {
        const fillingTaskName = variantName ? `${brandName} - ${variantName} - Filling` : `${brandName} - Filling`;
        await base44.entities.ProductionTask.update(currentTask.id, {
          task_name: fillingTaskName,
          product_name: currentTask.product_name || null,
          activity_type: 'filling',
          target_quantity: fillingTargetQty,
          unit: 'pcs',
          botol_ukuran_aktual: bottleSize,
          status: 'assigned',
          pic_id: null,
          pic_name: null,
          sub_stages: {
            ...(currentTask.sub_stages || {}),
            mixing: {
              status: "completed",
              pic_id: currentTask.pic_id,
              pic_name: currentTask.pic_name,
              end_time: new Date().toISOString(),
            },
          },
        });
      }
    } else if (currAct === 'filling') {
      if (poId) {
        await base44.entities.ProductionOrder.update(poId, { status: 'menunggu_packing' }).catch(() => {});
      }

      const bottleSize = Number(currentTask.botol_ukuran_aktual || currentTask.ukuran_ml || 30);
      const isSourceLiter = (currentTask.unit || '').toLowerCase().includes('l') || (currentTask.unit || '').toLowerCase().includes('liter');
      
      // Target pengemasan WAJIB menggunakan botol jadi (good bottles) dari output filling riil
      const goodBottles = Number(completedQty) > 0 
        ? Number(completedQty)
        : (Number(currentTask.completed_quantity) > 0 
            ? Number(currentTask.completed_quantity) 
            : (isSourceLiter ? Math.round((Number(currentTask.target_quantity) || 0) * 1000 / bottleSize) : Math.round(Number(currentTask.target_quantity) || 1)));

      const packingTargetQty = Math.max(1, Math.round(goodBottles));

      // Cek apakah tugas pengemasan sudah ada untuk PO & varian ini (Cegah Duplikasi)
      const existingPacking = allPoTasks.find(t => 
        t.id !== currentTask.id &&
        ['packing', 'pengemasan', 'packaging'].includes(t.activity_type) &&
        (!variantName || !t.product_name || t.product_name.toLowerCase().trim() === variantName.toLowerCase().trim())
      );

      if (existingPacking) {
        // Jika tugas pengemasan sudah ada (misal curi start atau dibuat di awal), perbarui targetnya ke botol jadi
        await base44.entities.ProductionTask.update(existingPacking.id, {
          target_quantity: packingTargetQty,
          unit: 'pcs',
          botol_ukuran_aktual: bottleSize,
        });
      } else {
        const packingTaskName = variantName ? `${brandName} - ${variantName} - Pengemasan` : `${brandName} - Pengemasan`;
        await base44.entities.ProductionTask.update(currentTask.id, {
          task_name: packingTaskName,
          product_name: currentTask.product_name || null,
          activity_type: 'packing',
          target_quantity: packingTargetQty,
          unit: 'pcs',
          botol_ukuran_aktual: bottleSize,
          status: 'assigned',
          pic_id: null,
          pic_name: null,
          sub_stages: {
            ...(currentTask.sub_stages || {}),
            filling: {
              status: "completed",
              pic_id: currentTask.pic_id,
              pic_name: currentTask.pic_name,
              end_time: new Date().toISOString(),
            },
          },
        });
      }
    } else if (['packing', 'pengemasan', 'labeling', 'packaging', 'sealing'].includes(currAct)) {
      if (poId) {
        const isReady = await checkIfPOReadyForSiapKirim(poId);
        if (isReady) {
          await base44.entities.ProductionOrder.update(poId, { status: 'siap_kirim' }).catch(() => {});
        } else {
          // Jika belum semua varian/tugas selesai, tetap di tahap pengemasan (berjalan)
          await base44.entities.ProductionOrder.update(poId, { status: 'packing' }).catch(() => {});
        }
      }
    }
  } catch (err) {
    console.error('Error in autoAdvanceNextTask:', err);
  }
}

/**
 * Mengecek apakah suatu PO benar-benar sudah selesai 100% dan siap kirim:
 * 1. PO harus punya task dan SEMUA task harus berstatus 'completed' (tidak boleh ada yang assigned, in_progress, waiting_qc, waiting_filling)
 * 2. Jika PO memiliki multi-varian (products), SEMUA varian wajib memiliki task pengemasan/akhir yang tervalidasi selesai
 * 3. Jika belum semua selesai, PO TIDAK BOLEH masuk ke 'siap_kirim'
 */
export async function checkIfPOReadyForSiapKirim(poId) {
  if (!poId) return false;
  try {
    const orders = await base44.entities.ProductionOrder.filter({ id: poId });
    if (!orders || orders.length === 0) return false;
    const po = orders[0];

    const tasks = (await base44.entities.ProductionTask.filter({ po_id: poId })) || [];
    if (tasks.length === 0) return false;

    // 1. Semua tugas PO yang ada wajib selesai
    const hasUnfinishedTasks = tasks.some(t => !['completed', 'cancelled'].includes(t.status));
    if (hasUnfinishedTasks) return false;

    // 2. Cek varian / produk
    const products = po.products || [];
    if (products.length > 0) {
      const completedPackingTasks = tasks.filter(t => 
        ['packing', 'pengemasan', 'labeling', 'packaging', 'sealing', 'qc'].includes((t.activity_type || '').toLowerCase()) &&
        t.status === 'completed'
      );

      // Setiap varian harus sudah ada task pengemasan yang selesai
      const allVariantsDone = products.every(prod => {
        const pName = (prod.nama_produk || prod.product_name || prod.variant_name || prod.nama || '').toLowerCase().trim();
        if (!pName) return true;

        return completedPackingTasks.some(t => {
          const tProd = (t.product_name || t.variant_name || '').toLowerCase().trim();
          const tName = (t.task_name || '').toLowerCase().trim();
          if (tProd && (tProd === pName || tProd.includes(pName) || pName.includes(tProd))) return true;
          if (tName && tName.includes(pName)) return true;
          if (Array.isArray(t.variants) && t.variants.length > 0) {
            return t.variants.some(v => {
              const vName = (v.product_name || v.name || '').toLowerCase().trim();
              return vName === pName || vName.includes(pName) || pName.includes(vName);
            });
          }
          if (!t.product_name && (!t.variants || t.variants.length === 0)) {
            return completedPackingTasks.length >= products.length;
          }
          return false;
        });
      });

      if (!allVariantsDone) return false;
    } else {
      const hasCompletedPacking = tasks.some(t => 
        ['packing', 'pengemasan', 'labeling', 'packaging', 'sealing', 'qc'].includes((t.activity_type || '').toLowerCase()) &&
        t.status === 'completed'
      );
      if (!hasCompletedPacking) return false;
    }

    return true;
  } catch (err) {
    console.error('Error in checkIfPOReadyForSiapKirim:', err);
    return false;
  }
}

export async function createFillingTaskIfNeeded() {
  return;
}
